import type { PricingData } from "@/modules/corporate/types/pricing"
import type { PricingSection } from "@/modules/corporate/types/sections"
import type { CSSProperties, FunctionComponent } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { Heading } from "@jobteaser/spark/components/Heading"
import { Text } from "@jobteaser/spark/components/Text"
import { Layout } from "@jobteaser/ui-kit/components/Layout/Layout"

import { ColGroup } from "@/modules/corporate/components/Sections/Pricing/components/ColGroup/ColGroup"
import { FeaturePackCell } from "@/modules/corporate/components/Sections/Pricing/components/FeaturePackCell/FeaturePackCell"
import { RecruitAdInsert } from "@/modules/corporate/components/Sections/Pricing/components/RecruitAdInsert/RecruitAdInsert"
import { ThPackHeading } from "@/modules/corporate/components/Sections/Pricing/components/ThPackHeading/ThPackHeading"
import { TrSection } from "@/modules/corporate/components/Sections/Pricing/components/TrSection/TrSection"
import { mobileReversePackOrderStyle } from "@/modules/corporate/components/Sections/Pricing/utils/mobileReversePackOrderStyle"
import { URL_PATHNAME } from "@/modules/corporate/constants"
import pricing from "@/modules/corporate/data/pricing.json"
import teasyDual from "@/modules/corporate/images/teasy-outline-dual.svg"
import teasyParallel from "@/modules/corporate/images/teasy-outline-parallel.svg"
import { useCorporateTrackingClick } from "@/modules/corporate/tracking/useCorporateTrackingClick"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { Link as NextLink } from "@/modules/routing/components/Link"
import { getAssetImportUrl } from "@/modules/staticAssets/getAssetImportUrl"

import styles from "./Pricing.module.css"

export type PricingPropsType = {
  section: PricingSection
}

const PRICING_DATA = pricing as unknown as PricingData

export const Pricing: FunctionComponent<PricingPropsType> = ({ section: { contactPage } }) => {
  const locale = useLocale()
  const trackClickEvent = useCorporateTrackingClick()
  const { t } = useTranslation(["corporate"])

  const teasyParallelUrl = `${getAssetImportUrl(teasyParallel)}`
  const teasyDualUrl = `${getAssetImportUrl(teasyDual)}`
  const layoutStyle = {
    "--backgroundDual": `url(${teasyDualUrl})`,
    "--backgroundParallel": `url(${teasyParallelUrl})`,
  } as CSSProperties

  const contactUrl = `/${locale}/${URL_PATHNAME}/${contactPage[0].slug}`

  const handleClick = (): void => {
    trackClickEvent("corporate_cta_pricing", {
      clicked_url: contactUrl,
    })
  }

  return (
    <Layout className={styles.layout}>
      <Layout.Column className={styles.layoutColumn} style={layoutStyle}>
        <section aria-labelledby="subscription-plans">
          <header className={styles.heading}>
            <Heading id="subscription-plans" variant="display1">
              {t("corporate.pricing.title")}
            </Heading>
            <Text variant="body1">{t("corporate.pricing.subtitle")}</Text>
            <Button renderComponent={NextLink} href={contactUrl} onClick={handleClick} variant="highlight">
              {t("corporate.pricing.cta")}
            </Button>
          </header>

          <table className={styles.subscriptionTable}>
            <ColGroup packs={PRICING_DATA.packs} />
            <thead>
              <tr>
                <th />
                {PRICING_DATA.packs.map((pack, packIndex) => (
                  <ThPackHeading
                    style={mobileReversePackOrderStyle(PRICING_DATA.packs.length, packIndex)}
                    key={pack.id}
                    pack={pack}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {PRICING_DATA.optionsFeature.map(feature =>
                feature.section ? (
                  <TrSection
                    key={feature.id}
                    title={feature.title ? t(feature.title) : ""}
                    length={PRICING_DATA.packs.length}
                  />
                ) : (
                  <tr className={styles.featureRows} key={feature.id}>
                    <th className={styles.headingRow} style={mobileReversePackOrderStyle()} scope="row">
                      {t(feature.title)}
                      {feature.description && (
                        <Text variant="body2" weight="regular">
                          {t(feature.description)}
                        </Text>
                      )}
                    </th>
                    {PRICING_DATA.packs.map((pack, packIndex) => (
                      <FeaturePackCell
                        style={mobileReversePackOrderStyle(PRICING_DATA.packs.length, packIndex)}
                        data-label={t(pack.title)}
                        feature={PRICING_DATA.featuresByPack[feature.id][pack.id]}
                        key={pack.id}
                      />
                    ))}
                  </tr>
                )
              )}
            </tbody>
          </table>
          <RecruitAdInsert
            title={t("corporate.pricing.recruit_ad_insert.title")}
            description={t("corporate.pricing.recruit_ad_insert.description")}
            cta={{ href: contactUrl, label: t("corporate.pricing.recruit_ad_insert.cta"), onClick: handleClick }}
          />
        </section>
      </Layout.Column>
    </Layout>
  )
}
